import {useEffect, useState} from "react";
import {useAppDensity} from "../app/AppContext";
import {OtsActionButton} from "../components/OtsButton";
import {OtsDataTableEmpty} from "../components/OtsDataTableCells";
import {OtsDialog} from "../components/OtsDialog";
import {OtsInlineAlert} from "../components/OtsInlineAlert";
import type {OtsBrand, OtsColorSchemePreference} from "../theme/OtsTheme";
import {GAUGE_QUANTITY_PREVIEW_ID, GaugeQuantityAuditList} from "./GaugeQuantityAuditList";
import {GaugeQuantityResultDetails} from "./GaugeQuantityResultDetails";
import type {GaugeQuantityAudit, GaugeQuantityResult} from "./gauge-quantity-types";

export type GaugeQuantityAuditDialogProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  /** Newest first. */
  audits?: GaugeQuantityAudit[];
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  /** Recalculates from current gauges and configuration without saving. */
  onPreview?: () => void;
  preview?: GaugeQuantityResult;
  previewing?: boolean;
  previewError?: string;
  /** Smaller dialog chrome, list, and details for embedded hosts such as WaterGo. Defaults to the app density. */
  compact?: boolean;
};

/** Gauge quantity history for one ticket: each recorded calculation and the decision flow behind it. */
export const GaugeQuantityAuditDialog = ({
  open, onClose, title = "Gauge quantity", brand = "water-go", colorScheme = "system",
  audits = [], loading = false, error, onRetry, onPreview, preview, previewing = false, previewError, compact: compactProp,
}: GaugeQuantityAuditDialogProps) => {
  const density = useAppDensity();
  const compact = compactProp ?? density === "compact";
  const size = compact ? "compact" : "default";
  const [selectedId, setSelectedId] = useState<string>();
  useEffect(() => { if (preview) setSelectedId(GAUGE_QUANTITY_PREVIEW_ID); }, [preview]);
  const showingPreview = selectedId === GAUGE_QUANTITY_PREVIEW_ID && preview ? preview : undefined;
  const selected = showingPreview ? undefined : audits.find(audit => audit.id === selectedId) ?? audits[0];

  return <OtsDialog open={open} onClose={onClose} title={title} brand={brand} colorScheme={colorScheme} compact={compact}
    description="How the calculated gauge quantity was derived, or why it was not."
    actions={onPreview && <OtsActionButton size="compact" onClick={onPreview} disabled={previewing}>
      {previewing ? "Calculating…" : "Calculate now"}
    </OtsActionButton>}>
    {previewError && <OtsInlineAlert className="mb-3" size={size} tone="danger" title="Could not calculate">{previewError}</OtsInlineAlert>}
    {loading ? (
      <OtsDataTableEmpty compact={compact} loading loadingText="Loading calculation history…">{null}</OtsDataTableEmpty>
    ) : error ? (
      <OtsInlineAlert size={size} tone="danger" title="Calculation history is unavailable"
        action={onRetry && <OtsActionButton size="compact" onClick={onRetry}>Try again</OtsActionButton>}>
        {error}
      </OtsInlineAlert>
    ) : !audits.length && !preview ? (
      <OtsDataTableEmpty compact={compact}>
        No calculations have been recorded for this ticket.
        {onPreview && " Use Calculate now to see how its current gauges would be calculated."}
      </OtsDataTableEmpty>
    ) : (
      <div className={compact ? "grid gap-3 @3xl:grid-cols-[13rem_minmax(0,1fr)]" : "grid gap-4 @3xl:grid-cols-[16rem_minmax(0,1fr)]"}>
        <GaugeQuantityAuditList compact={compact} audits={audits} previewAvailable={!!preview}
          selectedId={showingPreview ? GAUGE_QUANTITY_PREVIEW_ID : selected?.id} onSelect={setSelectedId}/>
        {showingPreview
          ? <GaugeQuantityResultDetails compact={compact} result={showingPreview}/>
          : selected && <GaugeQuantityResultDetails compact={compact} result={selected.result} storedQuantity={selected.storedQuantity ?? null}/>}
      </div>
    )}
  </OtsDialog>;
};
